type RgbColor = {
  red: number;
  green: number;
  blue: number;
};

const parseHexColor = (color: string): RgbColor | null => {
  const hex = color.replace('#', '');

  if (hex.length === 3) {
    return {
      red: parseInt(hex[0] + hex[0], 16),
      green: parseInt(hex[1] + hex[1], 16),
      blue: parseInt(hex[2] + hex[2], 16),
    };
  }

  if (hex.length === 6 || hex.length === 8) {
    return {
      red: parseInt(hex.slice(0, 2), 16),
      green: parseInt(hex.slice(2, 4), 16),
      blue: parseInt(hex.slice(4, 6), 16),
    };
  }

  return null;
};

const parseRgbChannel = (channel: string): number => {
  if (channel.endsWith('%')) {
    return (Number(channel.replace('%', '')) / 100) * 255;
  }

  return Number(channel);
};

const parseRgbColor = (color: string): RgbColor | null => {
  const match = color.match(/^rgba?\((.+)\)$/i);
  if (!match) return null;

  const channels = match[1]
    .replace(/\//g, ' ')
    .split(/[,\s]+/)
    .filter(Boolean);
  if (channels.length < 3) return null;

  return {
    red: parseRgbChannel(channels[0]),
    green: parseRgbChannel(channels[1]),
    blue: parseRgbChannel(channels[2]),
  };
};

const parseColor = (color: string): RgbColor | null => {
  const normalizedColor = color.trim();
  const parsedColor = normalizedColor.startsWith('#')
    ? parseHexColor(normalizedColor)
    : parseRgbColor(normalizedColor);

  if (!parsedColor) return null;

  return Object.values(parsedColor).every(Number.isFinite) ? parsedColor : null;
};

const getPerceivedBrightness = ({ red, green, blue }: RgbColor): number =>
  (red * 0.299 + green * 0.587 + blue * 0.114) / 255;

export const isBackgroundDark = (element: HTMLDivElement | null): boolean => {
  if (!element) return false;

  const backgroundColor = getComputedStyle(element).getPropertyValue('--background-color');
  const color = parseColor(backgroundColor);
  if (!color) return false;

  return getPerceivedBrightness(color) < 0.5;
};
